import { createSlice } from '@reduxjs/toolkit'
import { API } from 'src/utils/interceptor'
import dayjs from 'dayjs'

const initialState = {
  totalSaleWeek: null,
  GP: '',
  GPExclMisc: '',
  totalMiscCosts: '',
  totalSaleAmount: '',
  excludedThisPeriod: { productCount: 0, spend: 0 },
  dateRange: null,
  customMode: false,
  isLoading: false,
}

const weeklySlice = createSlice({
  name: 'sale',
  initialState,
  reducers: {
    setGrossProfit: (state, action) => {
      state.GP = action.payload
    },
    setGrossProfitExclMisc: (state, action) => {
      state.GPExclMisc = action.payload
    },
    setTotalMiscCosts: (state, action) => {
      state.totalMiscCosts = action.payload
    },
    setTotalSaleAmount: (state, action) => {
      state.totalSaleAmount = action.payload
    },
    setExcludedThisPeriod: (state, action) => {
      state.excludedThisPeriod = action.payload || {
        productCount: 0,
        spend: 0,
      }
    },
    setTotalSaleWeek: (state, action) => {
      state.totalSaleWeek = action.payload
    },
    setCustomMode: (state, action) => {
      state.customMode = action.payload
    },
    setDateRange: (state, action) => {
      state.dateRange = action.payload
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
  },
})

export const {
  setTotalSaleWeek,
  setCustomMode,
  setDateRange,
  setIsLoading,
  setGrossProfit,
  setGrossProfitExclMisc,
  setTotalMiscCosts,
  setTotalSaleAmount,
  setExcludedThisPeriod,
} = weeklySlice.actions

export const fetchGrossProfitAndSales =
  (from, to, venueId, options = {}) =>
  async dispatch => {
    const silent = Boolean(options.silent)
    if (!silent) dispatch(setIsLoading(true))
    return API.get(
      'api',
      `gross-profit/sales-per-day?fromDate=${from}&toDate=${to}&venue=${venueId}`,
    )
      .then(data => {
        const formatedData = data?.salesPerDay?.map((obj, index) => ({
          ...obj,
          date: dayjs(obj.date).format('YYYY-MM-DD'),
          day: dayjs(obj.date).format('dddd'),
          key: 'item-' + index,
        }))
        dispatch(setTotalSaleWeek(formatedData || []))
        dispatch(setGrossProfit(data?.gpp ?? ''))
        dispatch(setGrossProfitExclMisc(data?.gppExclMisc ?? ''))
        dispatch(setTotalMiscCosts(data?.totalMiscCosts ?? ''))
        dispatch(setTotalSaleAmount(data?.totalSaleAmount ?? ''))
        dispatch(
          setExcludedThisPeriod(
            data?.excludedThisPeriod || { productCount: 0, spend: 0 },
          ),
        )
        if (!silent) dispatch(setIsLoading(false))
        return formatedData
      })
      .catch(error => {
        if (!silent) {
          dispatch(setIsLoading(false))
          dispatch(setTotalSaleWeek([]))
          dispatch(setGrossProfit(''))
          dispatch(setGrossProfitExclMisc(''))
          dispatch(setExcludedThisPeriod({ productCount: 0, spend: 0 }))
        }
        console.error(error)
        return null
      })
  }

export const fetchGrossProfitAndSalesForVenues =
  (fromDate, toDate, venues) => async dispatch => {
    dispatch(setIsLoading(true))
    try {
      const response = await API.post(
        'api2',
        `gross-profit/get-sales-data-for-venues`,
        {
          body: {
            fromDate,
            toDate,
            venues,
          },
        },
      )

      return response.data
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const sendSalesOrPurchaseData =
  (venueId, body, dateRange, options = {}) =>
  async dispatch => {
    const silent = Boolean(options.silent)
    if (!silent) dispatch(setIsLoading(true))
    return API.post('api', `gross-profit/${venueId}`, {
      body,
    })
      .then(() => {
        if (!silent) dispatch(setIsLoading(false))
        return dispatch(
          fetchGrossProfitAndSales(
            dateRange.startDate,
            dateRange.endDate,
            venueId,
            { silent },
          ),
        )
      })
      .then(() => null)
      .catch(error => {
        if (!silent) dispatch(setIsLoading(false))
        console.error(error)
        // Surface the failure so UI can show feedback instead of silently succeeding
        throw error
      })
  }

export const deleteSalesOrPurchaseData =
  (salesId, dateRange, venueId) => async dispatch => {
    dispatch(setIsLoading(true))
    return API.del('api', `gross-profit/sales/${salesId}`)
      .then(() => {
        // dispatch(setIsLoading(false))
        dispatch(
          fetchGrossProfitAndSales(
            dateRange.startDate,
            dateRange.endDate,
            venueId,
          ),
        )
        return null
      })
      .catch(error => {
        dispatch(setIsLoading(false))
        console.error(error)
        return null
      })
  }

export default weeklySlice
